import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { FiMenu, FiBell } from "react-icons/fi";
import { FaSignOutAlt, FaKey, FaUsers } from "react-icons/fa";
import toast from "react-hot-toast";
import { clearToken, getToken, stopTokenTimer } from "../Services/tokenUtils";
import API from "../Services/BaseUrl";

/**
 * Maps each route to the page title shown in the navbar. Keys match the
 * `path` values registered in App.js and Sidebar.jsx; if a route ever ships
 * without an entry here we fall back to a generic "Stock Management" label.
 */
const PAGE_TITLES = {
  "/dashboard":          "Dashboard",
  "/company":            "Company",
  "/stores":             "Stores",
  "/vendors":            "Vendors",
  "/customers":          "Customers",
  "/categories":         "Categories",
  "/products":           "Products",
  "/inventory":          "Inventory",
  "/inventory-tracking": "Inventory Tracking",
  "/stock-in":           "Stock In",
  "/stock-out":          "Stock Out",
  "/purchase":           "Purchase Orders",
  "/feeder":             "Feeder Management",
  "/bom":                "BOM Management",
  "/jobs":               "Job Management",
  "/user-management":    "User Management",
  "/user-roles":         "User Roles",
  "/audit":              "Audit",
  "/change-password":    "Change Password",
};

function pageTitleFor(pathname) {
  if (!pathname) return "Stock Management";
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  // Tolerate trailing slashes and unknown nested routes — fall back to first
  // segment with the first letter capitalised so the navbar still reads well.
  const seg = pathname.replace(/^\/+/, "").split("/")[0];
  if (!seg) return "Stock Management";
  return seg
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Top navigation bar.
 *
 * Props:
 *   onToggleSidebar — called when the hamburger button is clicked;
 *                     Layout decides whether to collapse (desktop) or open
 *                     the overlay (mobile).
 */
export default function Navbar({ onToggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [loggedUser, setLoggedUser] = useState({ fullName: "", roleName: "" });
  const [notificationCount] = useState(0); // placeholder — wire up to real data when ready

  // Avatar dropdown state.  The dropdown panel is portaled to document.body
  // so it escapes every ancestor's stacking context / overflow clip — the
  // tables inside <main> have a sticky <thead> at z-10 that was painting
  // over an inline dropdown on desktop.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);          // trigger button wrapper (for click-outside)
  const triggerRef = useRef(null);       // the actual avatar button (used to anchor the portal)
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  // Logout confirmation dialog state.
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    const value = localStorage.getItem("loggedUser");
    if (!value || value === "undefined" || value === "null") return;
    try {
      const user = JSON.parse(value);
      if (user) {
        setLoggedUser({
          fullName: user.fullName || "-",
          roleName: user.roleName || "-",
        });
      }
    } catch (err) {
      console.error("Error parsing loggedUser JSON:", err);
    }
  }, []);

  // Close the avatar dropdown when clicking outside / pressing ESC.
  // Note: the portal renders outside menuRef's DOM subtree, so click-outside
  // must accept clicks on the portal too — we tag the panel with a data
  // attribute and special-case it here.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      const onTrigger = menuRef.current && menuRef.current.contains(e.target);
      const onPanel = e.target.closest?.("[data-navbar-menu-panel]");
      if (!onTrigger && !onPanel) setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  // Position the portaled dropdown directly below the trigger.  We refresh
  // on open + on resize so the panel tracks viewport changes.
  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) return undefined;
    const updatePos = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,                // 4px gap below the trigger
        right: window.innerWidth - rect.right, // anchor by the right edge
      });
    };
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [menuOpen]);

  // Auto-close the menu whenever the route changes — keeps the dropdown from
  // hanging open after a Change Password navigation.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const pageTitle = pageTitleFor(location.pathname);
  const avatarLetter = loggedUser.fullName ? loggedUser.fullName.charAt(0).toUpperCase() : "U";

  const handleLogoutOthers = async () => {
    setMenuOpen(false);
    try {
      const token = getToken();
      const res = await API.post("/users/logoutOtherSessions", null, {
        headers: { Authorization: token },
      });
      const msg = res?.data?.message || "Signed out your other sessions";
      if (res?.data?.errCode === 0) toast.success(msg);
      else toast.error(msg);
    } catch (error) {
      console.error("Logout other sessions failed:", error);
      toast.error("Failed to sign out other sessions");
    }
  };

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      const token = getToken();
      if (token) {
        await API.post("/users/logout", null, { headers: { Authorization: token } });
      }
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      stopTokenTimer();
      clearToken();
      localStorage.setItem("logout", Date.now().toString());
      setShowLogoutDialog(false);
      setLoggingOut(false);
      navigate("/login");
    }
  };

  return (
    <>
      <div className="w-full bg-[#4682B4] text-white px-2 sm:px-4 py-2 shadow z-10 flex items-center min-h-[42px] flex-shrink-0">
        {/* LEFT — sidebar toggle */}
        <div className="flex items-center w-auto shrink-0">
          <button
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            className="text-white hover:opacity-80 transition-opacity p-1 rounded"
          >
            <FiMenu size={22} />
          </button>
        </div>

        {/* LEFT — page title (driven by current route) */}
        <div className="flex-1 min-w-0 px-3 sm:px-4">
          <h1 className="text-base sm:text-lg font-semibold tracking-wide truncate" title={pageTitle}>
            {pageTitle}
          </h1>
        </div>

        {/* RIGHT — notifications + avatar with dropdown */}
        <div className="flex items-center gap-2 sm:gap-3 w-auto shrink-0">
          {/* Notification bell with badge */}
          <button
            aria-label="Notifications"
            className="relative text-white hover:opacity-80 transition-opacity p-1"
          >
            <FiBell size={20} />
            {notificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center">
                {notificationCount > 9 ? "9+" : notificationCount}
              </span>
            )}
          </button>

          {/* User name + role — visible on sm+, also clickable to open the
              same dropdown as the avatar. The dropdown itself is portaled to
              document.body (see render block below) so it can't be clipped by
              ancestor overflow or out-stacked by sticky table headers. */}
          <div ref={menuRef} className="relative">
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="User menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="flex items-center gap-2 hover:opacity-90 transition-opacity rounded"
            >
              <div className="hidden sm:flex flex-col text-right text-xs leading-tight">
                <span className="font-medium truncate max-w-[120px] text-sm">
                  {loggedUser.fullName}
                </span>
                <span className="opacity-75 truncate max-w-[120px] text-[11px]">
                  {loggedUser.roleName}
                </span>
              </div>
              <div className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-xs font-semibold select-none">
                {avatarLetter}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Avatar dropdown — portaled to document.body so it escapes the
          layout's overflow-hidden ancestors and any stacking context
          created by sticky table <thead> rows beneath the navbar. */}
      {menuOpen && ReactDOM.createPortal(
        <div
          data-navbar-menu-panel
          role="menu"
          style={{ position: "fixed", top: menuPos.top, right: menuPos.right }}
          className="w-56 bg-white text-gray-800 rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden z-[100]"
        >
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-sm font-semibold text-gray-800 truncate">
              {loggedUser.fullName}
            </p>
            <p className="text-xs text-gray-500 truncate">{loggedUser.roleName}</p>
          </div>
          <button
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              navigate("/change-password");
            }}
            className="w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
          >
            <FaKey className="text-gray-500" size={12} />
            Change Password
          </button>
          <button
            role="menuitem"
            onClick={handleLogoutOthers}
            className="w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
          >
            <FaUsers className="text-gray-500" size={12} />
            Logout Other Sessions
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              setShowLogoutDialog(true);
            }}
            className="w-full px-3 py-2.5 text-sm text-left hover:bg-red-50 text-red-600 flex items-center gap-3 transition-colors border-t border-gray-100"
          >
            <FaSignOutAlt size={12} />
            Logout
          </button>
        </div>,
        document.body
      )}

      {/* Logout confirmation dialog — mirrors the one in Sidebar so logout
          works the same whether triggered from the sidebar or the avatar. */}
      {showLogoutDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[100]">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirm Logout</h2>
            <p className="text-gray-600 mb-6">Are you sure you want to logout?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleLogoutConfirm}
                disabled={loggingOut}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                {loggingOut ? "Logging out..." : "Yes"}
              </button>
              <button
                onClick={() => setShowLogoutDialog(false)}
                disabled={loggingOut}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md disabled:opacity-50"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
